/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
/* eslint-disable no-await-in-loop */

import React from 'react';
import { decorate } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Icon, Popup } from 'semantic-ui-react';

import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import { displayError, displayWarning } from '@amzn/base-ui/dist/helpers/notification';
import { httpApiGet } from '@amzn/base-ui/dist/helpers/api';
import { isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import BudgetHeatMap from './graphs/BudgetHeatMap';
import BudgetCards from './graphs/BudgetCards';
import WorkspaceOverviewWidget from './widgets/WorkspaceOverviewWidget';
import AnnouncementsWidget from './widgets/AnnouncementsWidget';
import WelcomeWidget from './widgets/WelcomeWidget';
import { getUserProjectBudgets } from '../../helpers/api';

const WELCOME_STORAGE_KEY = 'swb-welcome-widget-dismissed';
const iconStyle = { margin: 0, lineHeight: 1, height: '1em' };

class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      budgets: {},
      announcements: [],
      announcementsAvailable: true,
      announcementsLoading: true,
      workspacesLoading: true,
      budgetCardsExpanded: false,
      welcomeDismissed: (() => { try { return localStorage.getItem(WELCOME_STORAGE_KEY) === 'true'; } catch (_) { return false; } })(),
    };
  }

  async componentDidMount() {
    window.scrollTo(0, 0);

    // Load budgets
    try {
      const budgets = await getUserProjectBudgets();
      this.setState({ isLoading: false, budgets });
    } catch (error) {
      const store = this.getStore();
      if (error.code === 'AccessDeniedException') {
        if (store.user.isAdmin) {
          displayWarning(
            'Error encountered accessing cost data. Please enable Cost Explorer in the AWS Management Console and wait for 24 hours.',
          );
        }
      } else {
        displayError(error.message);
      }
      this.setState({ isLoading: false });
    }

    // Load announcements
    try {
      const announcements = await httpApiGet('api/announcements');
      this.setState({ announcements, announcementsAvailable: true, announcementsLoading: false });
    } catch (error) {
      if (error.status === 404) {
        this.setState({ announcementsAvailable: false, announcementsLoading: false });
      } else {
        displayError(error);
        this.setState({ announcementsLoading: false });
      }
    }

    // Load workspaces for the overview widget
    try {
      const envStore = this.props.scEnvironmentsStore;
      if (envStore && !isStoreReady(envStore)) {
        await envStore.load();
      }
      this.setState({ workspacesLoading: false });
    } catch (error) {
      // Silently handle - widget will show empty state
      this.setState({ workspacesLoading: false });
    }
  }

  getStore() {
    return this.props.userStore;
  }

  goto(path) {
    const goto = gotoFn(this);
    goto(path);
  }

  render() {
    const isAdmin = this.getStore().user.isAdmin;
    const showAsResearcher = isAdmin && this.props.userStore.researcherView;

    return (
      <Container className="mt3 mb4">
          {/* ── Welcome widget for new users ── */}
          <WelcomeWidget
            dismissed={this.state.welcomeDismissed}
            onDismiss={() => {
              this.setState({ welcomeDismissed: true });
              try { localStorage.setItem(WELCOME_STORAGE_KEY, 'true'); } catch (_) { /* noop */ }
            }}
            onGoToDocs={() => this.goto('/documentation')}
            onGoToWorkspaces={() => this.goto('/workspaces/select-studies')}
          />

          {/* ── Top row: Announcements + Workspace Overview ── */}
          <div className="dashboard-top-row">
            {this.renderAnnouncementsWidget()}
            {this.renderWorkspaceOverviewWidget()}
          </div>

          {/* ── Admin-only: Budget Heatmap ── */}
          {isAdmin && !showAsResearcher && (
            <div className="dashboard-admin-section">
              {this.renderAdminHeatMap()}
            </div>
          )}

          {/* ── Projects grid: one card per project ── */}
          {this.renderProjectCards(showAsResearcher)}
        </Container>
    );
  }

  // ─── Announcements Widget ───
  renderAnnouncementsWidget() {
    const { announcements, announcementsLoading, announcementsAvailable } = this.state;
    return (
      <AnnouncementsWidget
        announcements={announcements}
        isLoading={announcementsLoading}
        isAvailable={announcementsAvailable}
      />
    );
  }

  // ─── Workspace Overview Widget ───
  renderWorkspaceOverviewWidget() {
    const envStore = this.props.scEnvironmentsStore;
    const { workspacesLoading } = this.state;

    return (
      <WorkspaceOverviewWidget
        scEnvironmentsStore={envStore}
        isLoading={workspacesLoading}
        onCreateWorkspace={() => this.goto('/workspaces/select-studies')}
        onViewWorkspaces={() => this.goto('/workspaces')}
      />
    );
  }

  // ─── Admin Heatmap ───
  renderAdminHeatMap() {
    const { budgets, isLoading } = this.state;

    return (
      <div className="workspace-card" style={{ overflow: 'hidden' }}>
        <div className="workspace-card-accent" style={{ background: 'var(--rmit-yellow)' }} />
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: '600', color: 'var(--rmit-blue)' }}>
              <Icon name="fire" style={{ ...iconStyle, marginRight: '8px', color: 'var(--rmit-yellow)' }} />
              Budget Heatmap
            </h3>
          </div>
          {isLoading ? (
            <ProgressPlaceHolder segmentCount={1} />
          ) : (
            <BudgetHeatMap budgets={budgets} hideTitle />
          )}
        </div>
      </div>
    );
  }

  // ─── Project Cards (one per project) ───
  renderProjectCards(showAsResearcher = false) {
    const { budgets, isLoading } = this.state;
    const isAdmin = this.getStore().user.isAdmin;

    if (isLoading) {
      return (
        <div className="workspace-card" style={{ overflow: 'hidden' }}>
          <div className="workspace-card-accent" style={{ background: 'var(--rmit-red)' }} />
          <div style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: '600', color: 'var(--rmit-blue)' }}>
                <Icon name="folder open" style={{ ...iconStyle, marginRight: '8px', color: 'var(--rmit-red)' }} />
                My Projects
              </h3>
            </div>
            <ProgressPlaceHolder segmentCount={1} />
          </div>
        </div>
      );
    }

    // For admins in normal view, collapsible
    if (isAdmin && !showAsResearcher) {
      return (
        <div className="workspace-card" style={{ overflow: 'hidden' }}>
          <div className="workspace-card-accent" style={{ background: 'var(--rmit-red)' }} />
          <div style={{ padding: '20px 24px' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => this.setState({ budgetCardsExpanded: !this.state.budgetCardsExpanded })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && this.setState({ budgetCardsExpanded: !this.state.budgetCardsExpanded })}
            >
              <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: '600', color: 'var(--rmit-blue)' }}>
                <Icon name="folder open" style={{ ...iconStyle, marginRight: '8px', color: 'var(--rmit-red)' }} />
                My Projects
              </h3>
              <Icon
                name={this.state.budgetCardsExpanded ? 'chevron up' : 'chevron down'}
                style={{ color: 'var(--rmit-grey)', lineHeight: 1, height: '1em', margin: 0 }}
              />
            </div>
            {this.state.budgetCardsExpanded && (
              <div style={{ marginTop: '20px' }}>
                <BudgetCards budgets={budgets} isAdmin={true} hideTitle />
              </div>
            )}
          </div>
        </div>
      );
    }

    // For researchers
    return (
      <div className="workspace-card" style={{ overflow: 'hidden' }}>
        <div className="workspace-card-accent" style={{ background: 'var(--rmit-red)' }} />
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: '600', color: 'var(--rmit-blue)' }}>
              <Icon name="folder open" style={{ ...iconStyle, marginRight: '8px', color: 'var(--rmit-red)' }} />
              My Projects
            </h3>
          </div>
          <BudgetCards budgets={budgets} isAdmin={false} hideTitle />
        </div>
      </div>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(Dashboard, {});

export default inject('userStore', 'scEnvironmentsStore')(withRouter(observer(Dashboard)));

// ─── Legacy utility functions (preserved for tests) ───

async function getCosts(getEnvironmentsFn, getEnvironmentCostFn) {
  const { envIdToCostInfo, envIdToEnvMetadata, duplicateEnvNames } = await getAccumulatedEnvCost(
    getEnvironmentsFn,
    getEnvironmentCostFn,
  );

  const indexNameToUserTotalCost = {};
  Object.keys(envIdToCostInfo).forEach(envId => {
    const indexName = envIdToEnvMetadata[envId].index;
    if (indexNameToUserTotalCost[indexName] === undefined) {
      indexNameToUserTotalCost[indexName] = {};
    }
    Object.keys(envIdToCostInfo[envId].pastMonthCostByUser).forEach(user => {
      const currentUserCost = indexNameToUserTotalCost[indexName][user] || 0;
      indexNameToUserTotalCost[indexName][user] = currentUserCost + envIdToCostInfo[envId].pastMonthCostByUser[user];
    });
  });

  const indexNameToTotalCost = {};
  let totalCost = 0;
  Object.keys(indexNameToUserTotalCost).forEach(indexName => {
    let indexCost = 0;
    Object.keys(indexNameToUserTotalCost[indexName]).forEach(user => {
      indexCost += indexNameToUserTotalCost[indexName][user];
    });
    totalCost += indexCost;
    indexNameToTotalCost[indexName] = indexCost;
  });

  return {
    totalCost,
    indexNameToTotalCost,
    indexNameToUserTotalCost,
    envIdToCostInfo,
    envIdToEnvMetadata,
    duplicateEnvNames,
  };
}

function getLabels(envIdToCostInfo, envIdToEnvMetadata, duplicateEnvNames) {
  const labels = Object.keys(envIdToCostInfo).map(envId => {
    const envName = envIdToEnvMetadata[envId].name;
    if (duplicateEnvNames.has(envName)) {
      return `${envName}: ${envId}`;
    }
    return envName;
  });
  return labels;
}

async function getAccumulatedEnvCost(getEnvironmentsFn, getEnvironmentCostFn) {
  const environments = await getEnvironmentsFn();
  const duplicateEnvNames = new Set();
  const envNameToEnvId = {};
  const envIdToEnvMetadata = {};
  environments.forEach(env => {
    if (env.isExternal) return;
    envIdToEnvMetadata[env.id] = {
      index: env.indexId,
      name: env.name,
    };
    if (envNameToEnvId[env.name] === undefined) {
      envNameToEnvId[env.name] = env.id;
    } else {
      duplicateEnvNames.add(env.name);
    }
  });

  const envIds = Object.keys(envIdToEnvMetadata);
  const envCostPromises = envIds.map(envId => {
    return getEnvironmentCostFn(envId, 30, false, true);
  });

  const envCostResults = await Promise.all(envCostPromises);
  const pastMonthCostByUserArray = envCostResults.map(costResult => {
    const createdByToCost = {};
    costResult.forEach(costDate => {
      const cost = costDate.cost;
      Object.keys(cost).forEach(group => {
        let createdBy = group.split('$')[1];
        createdBy = createdBy || 'None';
        const currentUserCost = createdByToCost[createdBy] || 0;
        createdByToCost[createdBy] = currentUserCost + cost[group].amount;
      });
    });
    return createdByToCost;
  });

  const yesterdayCostArray = envCostResults.map(costResult => {
    const yesterdayCost = costResult.length > 0 ? costResult[costResult.length - 1] : {};
    let totalCostVal = 0;
    if (yesterdayCost && yesterdayCost.cost) {
      Object.keys(yesterdayCost.cost).forEach(key => {
        totalCostVal += yesterdayCost.cost[key].amount;
      });
    }
    return totalCostVal;
  });

  const envIdToCostInfo = {};
  for (let i = 0; i < envIds.length; i++) {
    envIdToCostInfo[envIds[i]] = {
      pastMonthCostByUser: pastMonthCostByUserArray[i],
      yesterdayCost: yesterdayCostArray[i],
    };
  }

  return { envIdToCostInfo, envIdToEnvMetadata, duplicateEnvNames };
}

export { getAccumulatedEnvCost, getCosts, getLabels };
